import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Form, Grid, Header, Icon, Image, Input, Label,
  List, Message, Modal, Segment, TextArea
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { TeamMatchDto } from '../../app/models/Team';

// ── Availability Panel ─────────────────────────────────────────────────────────

interface AvailabilityProps { match: TeamMatchDto; }

export const TeamMatchAvailabilityPanel = observer(function TeamMatchAvailabilityPanel({ match }: AvailabilityProps) {
  const { teamMatchStore, userStore } = useStore();
  const { t } = useTranslation();
  const currentUserId = userStore.user?.userID;

  const myAvailability = match.availabilities.find(a => a.userId === currentUserId);
  const teamAMembers = match.availabilities.filter(a => a.teamId === match.teamAId);
  const teamBMembers = match.availabilities.filter(a => a.teamId === match.teamBId);

  const renderMemberList = (members: typeof teamAMembers, label: string) => (
    <div style={{ marginBottom: 16 }}>
      <Header as="h5" color="grey">{label}</Header>
      <List divided relaxed>
        {members.map(m => (
          <List.Item key={m.userId}>
            <Image avatar src={m.imageUrl || '/assets/user.png'} />
            <List.Content>
              <List.Header style={{ fontSize: '0.9em' }}>{m.fullName}</List.Header>
            </List.Content>
            <List.Content floated="right">
              <Label
                size="mini"
                color={m.status === 'Available' ? 'green' : m.status === 'Unavailable' ? 'red' : 'yellow'}
              >
                {t(`teamMatch.availability.${m.status.toLowerCase()}`)}
              </Label>
            </List.Content>
          </List.Item>
        ))}
        {members.length === 0 && (
          <p style={{ color: '#aaa', fontSize: '0.85em' }}>{t('teamMatch.availability.noMembers')}</p>
        )}
      </List>
    </div>
  );

  return (
    <Segment>
      {/* My response */}
      {myAvailability && (
        <Message
          color={myAvailability.status === 'Available' ? 'green' : myAvailability.status === 'Unavailable' ? 'red' : 'yellow'}
          style={{ marginBottom: 16 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>
              {t('teamMatch.availability.yourStatus')}:{' '}
              <strong>{t(`teamMatch.availability.${myAvailability.status.toLowerCase()}`)}</strong>
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="mini" positive
                loading={teamMatchStore.submitting}
                disabled={myAvailability.status === 'Available' || teamMatchStore.submitting}
                onClick={() => teamMatchStore.respondAvailability(match.id, true)}
              >
                <Icon name="check" />{t('teamMatch.availability.available')}
              </Button>
              <Button
                size="mini" basic color="red"
                loading={teamMatchStore.submitting}
                disabled={myAvailability.status === 'Unavailable' || teamMatchStore.submitting}
                onClick={() => teamMatchStore.respondAvailability(match.id, false)}
              >
                <Icon name="close" />{t('teamMatch.availability.unavailable')}
              </Button>
            </div>
          </div>
        </Message>
      )}

      <Grid stackable columns={2}>
        <Grid.Column>
          {renderMemberList(teamAMembers, match.teamAName)}
        </Grid.Column>
        {match.teamBName && (
          <Grid.Column>
            {renderMemberList(teamBMembers, match.teamBName)}
          </Grid.Column>
        )}
      </Grid>
    </Segment>
  );
});

// ── Result Panel ───────────────────────────────────────────────────────────────

interface ResultProps { match: TeamMatchDto; }

export const TeamMatchResultPanel = observer(function TeamMatchResultPanel({ match }: ResultProps) {
  const { teamMatchStore } = useStore();
  const { t } = useTranslation();
  const [scoreA, setScoreA] = useState('');
  const [scoreB, setScoreB] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [showDisputeForm, setShowDisputeForm] = useState(false);

  const result = match.result;

  // Report form (no result yet)
  if (!result && match.isCurrentUserCaptainOfAnyTeam) {
    return (
      <Segment>
        <Header as="h4">{t('teamMatch.result.reportTitle')}</Header>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{match.teamAName}</div>
            <Input
              type="number" min="0"
              value={scoreA}
              onChange={e => setScoreA(e.target.value)}
              style={{ width: 80, textAlign: 'center' }}
            />
          </div>
          <div style={{ fontSize: '1.5em', fontWeight: 900, color: '#999', alignSelf: 'flex-end', marginBottom: 6 }}>:</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{match.teamBName}</div>
            <Input
              type="number" min="0"
              value={scoreB}
              onChange={e => setScoreB(e.target.value)}
              style={{ width: 80, textAlign: 'center' }}
            />
          </div>
        </div>
        <Button
          primary
          loading={teamMatchStore.submitting}
          disabled={scoreA === '' || scoreB === '' || teamMatchStore.submitting}
          onClick={() => teamMatchStore.reportResult(match.id, Number(scoreA), Number(scoreB))}
        >
          <Icon name="send" />{t('teamMatch.result.report')}
        </Button>
      </Segment>
    );
  }

  if (!result) {
    return (
      <Segment>
        <Message info>
          <Icon name="info circle" />
          {t('teamMatch.result.noResult')}
        </Message>
      </Segment>
    );
  }

  const statusColor: Record<string, string> = {
    Reported: 'yellow', Confirmed: 'green', Disputed: 'red', Resolved: 'blue'
  };

  // Determine if current user can confirm/dispute (opposing captain)
  const canConfirmOrDispute = result.status === 'Reported' && match.isCurrentUserCaptainOfAnyTeam;

  return (
    <Segment>
      {/* Score display */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Label color={statusColor[result.status] as any || 'grey'} style={{ marginBottom: 12 }}>
          {t(`teamMatch.resultStatus.${result.status.toLowerCase()}`)}
        </Label>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 24, fontSize: '2em', fontWeight: 900
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.5em', color: '#666', marginBottom: 4 }}>{match.teamAName}</div>
            <span style={{ color: result.scoreA > result.scoreB ? '#21ba45' : '#333' }}>
              {result.scoreA}
            </span>
          </div>
          <span style={{ color: '#ccc' }}>:</span>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.5em', color: '#666', marginBottom: 4 }}>{match.teamBName}</div>
            <span style={{ color: result.scoreB > result.scoreA ? '#21ba45' : '#333' }}>
              {result.scoreB}
            </span>
          </div>
        </div>
        <div style={{ fontSize: '0.78em', color: '#888', marginTop: 8 }}>
          {t('teamMatch.result.reportedBy')}: {result.reportedByFullName}
        </div>
      </div>

      {result.disputeReason && (
        <Message warning>
          <Icon name="exclamation triangle" />
          <strong>{t('teamMatch.result.disputeReason')}: </strong>{result.disputeReason}
        </Message>
      )}

      {result.resolutionNote && (
        <Message info>
          <Icon name="gavel" />
          <strong>{t('teamMatch.result.resolution')}: </strong>{result.resolutionNote}
        </Message>
      )}

      {/* Confirm or dispute */}
      {canConfirmOrDispute && !showDisputeForm && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
          <Button positive loading={teamMatchStore.submitting}
            onClick={() => teamMatchStore.confirmOrDispute(match.id, true)}>
            <Icon name="check" />{t('teamMatch.result.confirm')}
          </Button>
          <Button negative basic onClick={() => setShowDisputeForm(true)}>
            <Icon name="exclamation triangle" />{t('teamMatch.result.dispute')}
          </Button>
        </div>
      )}

      {canConfirmOrDispute && showDisputeForm && (
        <div style={{ marginTop: 12 }}>
          <TextArea
            value={disputeReason}
            onChange={(_, d) => setDisputeReason(d.value as string)}
            placeholder={t('teamMatch.result.disputeReasonPlaceholder')}
            rows={3}
            style={{ width: '100%', marginBottom: 10, borderRadius: 8, border: '1px solid #ddd', padding: 10 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button negative loading={teamMatchStore.submitting}
              disabled={!disputeReason.trim() || teamMatchStore.submitting}
              onClick={() => teamMatchStore.confirmOrDispute(match.id, false, disputeReason)}>
              <Icon name="send" />{t('teamMatch.result.submitDispute')}
            </Button>
            <Button basic onClick={() => setShowDisputeForm(false)}>
              {t('teams.cancel')}
            </Button>
          </div>
        </div>
      )}
    </Segment>
  );
});

